import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  GroupService,
  InvitationRead,
  InvitationService,
  PatchedInvitationWrite,
} from '../../../../../js/api';
import { useCurrentUser } from '../../../../../js/app/hooks/auth';
import { groupQueryKeys } from '../../../../../js/app/query-keys-factories/groups';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { setFormErrorsFromApiErrors } from '../../../../../js/utils/forms';
import { BrandSwitch } from '../../../components/BrandSwitch';
import { Button } from '../../../components/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogCloseButton,
  DialogTitle,
} from '../../../components/dialog';
import { DropdownSelect } from '../../../components/dropdown-select';
import { Field, Label } from '../../../components/fieldset';
import { useCodeDefinitionsAsOptions } from '../../../hooks/useCodeDefinitions';
import { canOverrideRepGroups, getDefaultGroupsForRole, useRoleDefaults } from './roleDefaults';

interface EditInvitationPermissionsDialogProps {
  invitation: InvitationRead;
  open: boolean;
  onClose: () => void;
  queryKeyBase: string;
  showOrganization?: boolean;
}

interface EditInvitationFormData {
  role: string;
  group_names: string[];
}

export function EditInvitationPermissionsDialog({
  invitation,
  open,
  onClose,
  queryKeyBase,
  showOrganization = false,
}: EditInvitationPermissionsDialogProps) {
  const { user: currentUser } = useCurrentUser();
  const canOverride = canOverrideRepGroups(currentUser);

  const form = useForm<EditInvitationFormData>({
    defaultValues: { role: '', group_names: [] },
  });

  const permissionGroups = useQuery({
    queryKey: groupQueryKeys.list({ limit: 500 }),
    queryFn: async () => {
      const data = await GroupService.groupList({ limit: 500 });
      return data.results || [];
    },
    keepPreviousData: true,
    enabled: open,
  });

  const { data: roleOptions = [], isLoading: rolesLoading } =
    useCodeDefinitionsAsOptions('HealthcareEntityRepRole');

  useEffect(() => {
    if (!open) return;
    const groupNames = invitation.group_names ?? [];
    const cleanGroups =
      groupNames.length === 1 && groupNames[0] === null ? [] : groupNames.filter(Boolean);
    form.reset({
      role: invitation.role ?? '',
      group_names: cleanGroups as string[],
    });
  }, [open, invitation, form]);

  const { data: roleDefaultsMap } = useRoleDefaults();

  const applyRoleDefaults = (role: string) => {
    const apiGroupCodes = permissionGroups.data?.map((g) => g.code) ?? [];
    const defaults = getDefaultGroupsForRole(role, apiGroupCodes, roleDefaultsMap);
    form.setValue('group_names', defaults, { shouldDirty: true });
  };

  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (data: PatchedInvitationWrite) => {
      await InvitationService.invitationPartialUpdate({
        id: invitation.id,
        requestBody: data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeyBase],
      });
    },
  });

  const onSubmit = async (data: EditInvitationFormData) => {
    const submitData: PatchedInvitationWrite = {};
    if (canOverride) {
      submitData.role = data.role as PatchedInvitationWrite['role'];
      submitData.group_names = data.group_names;
    }

    try {
      await mutation.mutateAsync(submitData);
      toast.success('Invitation updated successfully.');
      onClose();
    } catch (errors) {
      setFormErrorsFromApiErrors(
        errors as Parameters<typeof setFormErrorsFromApiErrors>[0],
        form.setError
      );
      handleApiError(errors);
    }
  };

  const inviteeIdentifier = invitation.email || 'invited user';
  const isReadOnly = !canOverride;
  const titleAction = isReadOnly ? 'View' : 'Edit';
  const nameWithOrg =
    showOrganization && invitation.healthcare_entity_name
      ? `${inviteeIdentifier} at ${invitation.healthcare_entity_name}`
      : inviteeIdentifier;
  const dialogTitle = `${titleAction} permissions — ${nameWithOrg}`;

  return (
    <Dialog open={open} onClose={onClose} size="4xl">
      <form onSubmit={form.handleSubmit(onSubmit)} data-testid="edit-invitation-permissions-dialog">
        <div className="tw:flex tw:items-start tw:justify-between tw:gap-4">
          <DialogTitle data-testid="edit-invitation-permissions-dialog-title">
            {dialogTitle}
          </DialogTitle>
          {isReadOnly && <DialogCloseButton onClick={onClose} className="tw:-mr-1 tw:-mt-1" />}
        </div>
        <DialogBody>
          <div className="tw:space-y-6">
            <Field>
              <Label htmlFor="edit-invitation-role">Role</Label>
              <Controller
                name="role"
                control={form.control}
                render={({ field }) => (
                  <DropdownSelect
                    inputId="edit-invitation-role"
                    allOptions={roleOptions}
                    value={field.value}
                    onChange={(option) => {
                      const next = Array.isArray(option) ? option[0]?.value : option?.value;
                      const nextRole = next ?? '';
                      field.onChange(nextRole);
                      if (nextRole) applyRoleDefaults(nextRole);
                    }}
                    isDisabled={!canOverride}
                    isLoading={rolesLoading}
                    placeholder="Select a role..."
                  />
                )}
              />
            </Field>

            {permissionGroups.isLoading && (
              <div className="tw:flex tw:items-center tw:justify-center tw:py-8">
                <div
                  className="tw:h-8 tw:w-8 tw:animate-spin tw:rounded-full tw:border-2 tw:border-brand-green-0 tw:border-t-transparent"
                  aria-label="loading indicator"
                />
              </div>
            )}

            {permissionGroups.isError && (
              <div className="tw:rounded-lg tw:border tw:border-red-200 tw:bg-red-50 tw:px-4 tw:py-3 tw:text-red-700">
                <p className="tw:font-semibold">Something went wrong</p>
                <p className="tw:text-sm">We couldn&apos;t retrieve the list of permissions.</p>
              </div>
            )}

            {permissionGroups.isSuccess && permissionGroups.data && (
              <Controller
                name="group_names"
                control={form.control}
                render={({ field }) => (
                  <div className="tw:space-y-4">
                    {permissionGroups.data!.map((group) => {
                      const selected = Array.isArray(field.value)
                        ? field.value.includes(group.code)
                        : false;
                      return (
                        <div key={group.code} className="tw:flex tw:gap-4 tw:items-start">
                          <BrandSwitch
                            className="tw:!mt-1"
                            checked={selected}
                            disabled={!canOverride}
                            onChange={(checked) => {
                              if (!canOverride) return;
                              const current = Array.isArray(field.value) ? field.value : [];
                              if (checked) {
                                field.onChange([...current, group.code]);
                              } else {
                                field.onChange(current.filter((c) => c !== group.code));
                              }
                            }}
                          />
                          <div className="tw:flex tw:flex-col tw:gap-0.5 tw:min-w-0">
                            <span className="tw:font-medium">{group.title}</span>
                            {group.description && (
                              <span className="tw:text-xs tw:text-zinc-500">
                                {group.description}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              />
            )}
          </div>
        </DialogBody>
        {!isReadOnly && (
          <DialogActions>
            <Button type="button" plain onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              color="brandgreen1"
              disabled={mutation.isPending || permissionGroups.isLoading}
            >
              {mutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        )}
      </form>
    </Dialog>
  );
}
